import { getAuthHeaders, getAuthToken, getFreshToken, resolveEndpoint } from './api'
import { supabase } from './supabase'
import { getSignedUrl } from './storage'
import type { StructureSelection } from './structureTypes'
import { addStagingBreadcrumb } from './sentry/breadcrumbs'

/**
 * Deduct a credit from the user's profile
 * Used before starting a staging operation
 */
export async function deductCredit(userId: string): Promise<{ success: boolean; previousCredits?: number; error?: string }> {
  try {
    // Get current credits
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .single()

    if (fetchError || !profile) {
      return { success: false, error: 'Failed to fetch profile' }
    }

    const currentCredits = profile.credits || 0
    if (currentCredits < 1) {
      return { success: false, error: 'Insufficient credits' }
    }

    // Deduct credit
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ credits: currentCredits - 1 })
      .eq('id', userId)

    if (updateError) {
      return { success: false, error: 'Failed to deduct credit' }
    }

    return { success: true, previousCredits: currentCredits }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

/**
 * Refund a credit to the user's profile
 * Used when a staging operation fails after credit was deducted
 */
export async function refundCredit(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current credits
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .single()

    if (fetchError || !profile) {
      return { success: false, error: 'Failed to fetch profile' }
    }

    const currentCredits = profile.credits || 0

    // Refund credit
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ credits: currentCredits + 1 })
      .eq('id', userId)

    if (updateError) {
      return { success: false, error: 'Failed to refund credit' }
    }

    return { success: true }
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

/**
 * V1 Request (Legacy) - Full prompt sent from client
 * @deprecated Use AiStageRequestV2 for new implementations
 */
export type AiStageRequestV1 = {
  prompt: string
  constraints?: string
  variants?: number
  images?: string[]
  userId?: string
  imageId?: string
}

/**
 * Style source for V2 requests
 */
export type StyleSource =
  | { type: 'legacy'; slug: string }
  | { type: 'base'; styleId: string }
  | { type: 'user'; promptTemplate: string }

/**
 * V2 Request - Minimal identifiers, server constructs prompt
 * Hides proprietary prompts from browser network inspector
 */
export type AiStageRequestV2 = {
  apiVersion: '2.0'
  structureTypeId: string
  structureDomain: 'interior' | 'exterior'
  structureCategory?: string
  styleSource: StyleSource
  mode: 'stage' | 'declutter'
  customInstructions?: string
  clientRequestId?: string
  variants?: number
  images?: string[]
  userId: string
  imageId: string
}

export type AiStageRequestV3 = {
  apiVersion: '3.0'
  structureTypeId: string
  mode: 'stage' | 'declutter'
  styleSource?: StyleSource
  customInstructions?: string
  clientRequestId?: string
  variants?: number
  images?: string[]
  userId: string
  imageId: string
}

export type AiStageRequest = AiStageRequestV1 | AiStageRequestV2 | AiStageRequestV3

export type StageImageParams = {
  imageId: string
  originalUrl: string
  style: string
  structureSelection: StructureSelection
  /** Optional base style ID for tracking */
  baseStyleId?: string
  /** Optional user style ID for tracking */
  userStyleId?: string
  /** Optional user style prompt template (for user-created styles) */
  userStylePromptTemplate?: string
  /** Optional custom instructions from user */
  customInstructions?: string
  /** Staging mode - 'stage' or 'declutter' */
  mode?: 'stage' | 'declutter'
}

// Legacy params for backward compatibility
export type LegacyStageImageParams = {
  imageId: string
  originalUrl: string
  style: string
  roomType: string
}

export type StageImageResult = {
  stagedUrl: string
  stagedPath?: string
  imageId: string
  /** The ID of the staged_images record */
  stagedImageId: string
}

export type AiStageResponse = {
  images: { mimeType: string; data: string }[]
  text: string
}

export type StagingJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'canceled'

export type StagingJobRecord = {
  id: string
  user_id: string
  image_id: string
  status: StagingJobStatus
  idempotency_key: string
  request_payload: Record<string, unknown>
  attempts: number
  last_error: string | null
  result_staged_image_id: string | null
  queued_at: string
  started_at: string | null
  completed_at: string | null
  priority: number | null
  credit_transaction_id: string | null
  credit_usage_id: string | null
  credit_source: 'subscription' | 'profile' | null
}

export type StagingJobResponse = {
  job: StagingJobRecord
  stagedImage: { id: string; staged_url: string } | null
}

export type StagingJobStatusLiteRecord = Pick<
  StagingJobRecord,
  | 'id'
  | 'image_id'
  | 'status'
  | 'result_staged_image_id'
  | 'started_at'
  | 'completed_at'
  | 'attempts'
  | 'last_error'
> & {
  updated_at: string | null
}

export type StagingJobStatusLiteResponse = {
  job: StagingJobStatusLiteRecord
}

export type AiStageRequestV2Async = AiStageRequestV2 & {
  styleLabel?: string
  baseStyleId?: string | null
  userStyleId?: string | null
}

export type AiStageRequestV3Async = AiStageRequestV3 & {
  styleLabel?: string
  baseStyleId?: string | null
  userStyleId?: string | null
}

export type AiStageRequestAsync = AiStageRequestV2Async | AiStageRequestV3Async

function createClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

export type AiStageErrorDebug = {
  message?: string
  finishReasons?: string[]
  modelsTried?: string[]
  attempts?: number
  textResponse?: string
  reason?: string
}

export function getUserFriendlyStagingError(err: unknown): string {
  const error = err as { message?: string; reason?: string; debug?: AiStageErrorDebug }
  const reason = error?.reason || error?.debug?.reason

  if (reason === 'safety') {
    return 'This image could not be generated due to safety filters. Try a different photo or remove sensitive elements.'
  }
  if (reason === 'text_only') {
    return 'The AI returned text instead of an image. Please try again or simplify your instructions.'
  }
  if (reason === 'model_error') {
    return 'The AI model failed to generate an image. Please try again in a moment.'
  }

  return error?.message || 'No image was generated. Please try again.'
}

/**
 * Create an async staging job.
 */
export async function stageWithAIAsync(
  payload: AiStageRequestAsync,
  opts?: { authToken?: string },
): Promise<{ jobId: string }> {
  const endpoint = resolveEndpoint(payload.apiVersion === '3.0' ? '/ai/stage-v3-async' : '/ai/stage-async')
  const token = opts?.authToken || (await getAuthToken())
  if (!token) {
    throw new Error('Not authenticated')
  }

  const requestPayload: AiStageRequestAsync = payload.clientRequestId
    ? payload
    : { ...payload, clientRequestId: createClientRequestId() }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestPayload),
  })

  if (!response.ok) {
    let msg = `AI staging failed (${response.status})`
    const json = await response.json().catch(() => null) as {
      error?: string
      message?: string
      debug?: AiStageErrorDebug
      reason?: string
    } | null

    if (json) {
      msg = json.error || json.message || msg
      const err = new Error(msg) as Error & { debug?: AiStageErrorDebug; reason?: string }
      if (json.debug) err.debug = json.debug
      if (json.reason) err.reason = json.reason
      throw err
    }

    throw new Error(msg)
  }

  const data = await response.json() as { jobId: string }
  return data
}

/**
 * Fetch async staging job status.
 */
export async function getStagingJobStatus(
  jobId: string,
  opts?: { authToken?: string },
): Promise<StagingJobResponse> {
  const endpoint = resolveEndpoint(`/ai/job/${jobId}`)
  const token = opts?.authToken || (await getAuthToken())
  if (!token) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let msg = `Failed to fetch job (${response.status})`
    const json = await response.json().catch(() => null) as {
      error?: string
      message?: string
    } | null

    if (json) {
      msg = json.error || json.message || msg
    }

    throw new Error(msg)
  }

  return (await response.json()) as StagingJobResponse
}

/**
 * Fetch lightweight async staging job status.
 */
export async function getStagingJobStatusLite(
  jobId: string,
  opts?: { authToken?: string },
): Promise<StagingJobStatusLiteResponse> {
  const endpoint = resolveEndpoint(`/ai/job/${jobId}/status`)
  const token = opts?.authToken || (await getAuthToken())
  if (!token) {
    throw new Error('Not authenticated')
  }

  const response = await fetch(endpoint, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    let msg = `Failed to fetch job status (${response.status})`
    const json = await response.json().catch(() => null) as {
      error?: string
      message?: string
    } | null

    if (json) {
      msg = json.error || json.message || msg
    }

    const error = new Error(msg) as Error & { status?: number }
    error.status = response.status
    throw error
  }

  return (await response.json()) as StagingJobStatusLiteResponse
}

/**
 * Poll until async staging job completes.
 */
export async function waitForStagingJob(
  jobId: string,
  opts?: { authToken?: string; intervalMs?: number; timeoutMs?: number; useRealtime?: boolean },
): Promise<StagingJobResponse> {
  const intervalMs = opts?.intervalMs ?? 2000
  const timeoutMs = opts?.timeoutMs ?? 120000
  const useRealtime = opts?.useRealtime !== false

  const initial = await getStagingJobStatus(jobId, { authToken: opts?.authToken })
  if (initial.job.status === 'completed' || initial.job.status === 'failed' || initial.job.status === 'canceled') {
    return initial
  }

  return await new Promise((resolve, reject) => {
    let done = false
    let channel: ReturnType<typeof supabase.channel> | null = null

    const cleanup = () => {
      done = true
      if (channel) {
        supabase.removeChannel(channel)
      }
    }

    const timeoutId = setTimeout(() => {
      if (done) return
      cleanup()
      reject(new Error('Staging job timed out'))
    }, timeoutMs)

    const poll = async () => {
      if (done) return
      try {
        const status = await getStagingJobStatus(jobId, { authToken: opts?.authToken })
        if (status.job.status === 'completed' || status.job.status === 'failed' || status.job.status === 'canceled') {
          clearTimeout(timeoutId)
          cleanup()
          resolve(status)
          return
        }
      } catch (error) {
        void error
      }
      setTimeout(poll, intervalMs)
    }

    poll()

    if (useRealtime) {
      channel = supabase
        .channel(`staging-job-${jobId}`)
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'staging_jobs', filter: `id=eq.${jobId}` },
          payload => {
            const newJob = payload.new as StagingJobRecord
            if (newJob.status === 'completed' || newJob.status === 'failed' || newJob.status === 'canceled') {
              clearTimeout(timeoutId)
              cleanup()
              getStagingJobStatus(jobId, { authToken: opts?.authToken })
                .then(resolve)
                .catch(reject)
            }
          }
        )
        .subscribe()
    }
  })
}

/**
 * Stage an image using the async job system.
 */
export async function stageImageAsync(
  request: AiStageRequestAsync,
  opts?: { authToken?: string },
): Promise<StageImageResult> {
  const { jobId } = await stageWithAIAsync(request, opts)
  const result = await waitForStagingJob(jobId, opts)

  if (result.job.status !== 'completed') {
    throw new Error(result.job.last_error || 'Staging job failed')
  }

  if (!result.stagedImage?.staged_url || !result.stagedImage?.id) {
    throw new Error('Staged image not found')
  }

  return {
    stagedUrl: await Promise.resolve(result.stagedImage.staged_url),
    stagedPath: result.stagedImage.staged_url,
    imageId: result.job.image_id,
    stagedImageId: result.stagedImage.id,
  }
}

/**
 * Call the AI staging worker.
 *
 * If `authToken` is provided it will be used directly (avoids an extra
 * getSession round-trip). Otherwise falls back to getAuthHeaders().
 *
 * Retries once on network errors or 401 (expired token) by refreshing
 * the session before the second attempt.
 */
export async function stageWithAI(
  payload: AiStageRequest,
  opts?: { authToken?: string },
): Promise<AiStageResponse> {
  const endpoint = resolveEndpoint('/ai/stage')
  const MAX_ATTEMPTS = 2

  let lastError: Error | null = null

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    // Build auth header — prefer explicit token, refresh on retry
    let authHeader: string | undefined
    if (attempt === 1 && opts?.authToken) {
      authHeader = `Bearer ${opts.authToken}`
    } else if (attempt > 1) {
      // Retry path: force-refresh to get a new token
      try {
        const { token } = await getFreshToken()
        authHeader = `Bearer ${token}`
      } catch {
        // If we can't get a token at all, throw the original error
        throw lastError ?? new Error('Not authenticated')
      }
    } else {
      // No explicit token on first attempt — fall back to cached session
      const headers = await getAuthHeaders()
      authHeader = headers['Authorization']
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(authHeader ? { 'Authorization': authHeader } : {}),
    }

    let resp: Response
    try {
      resp = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })
    } catch {
      // Network-level failure (ERR_NETWORK_CHANGED, Failed to fetch, etc.)
      lastError = new Error(
        'Network connection was interrupted. Please check your connection and try again.'
      )
      if (attempt < MAX_ATTEMPTS) continue
      throw lastError
    }

    if (resp.status === 401 && attempt < MAX_ATTEMPTS) {
      // Token was expired/invalid — retry with a fresh one
      lastError = new Error('Session expired, retrying…')
      continue
    }

    if (!resp.ok) {
      let msg = `AI staging failed (${resp.status})`
      let debugError: (Error & { debug?: AiStageErrorDebug; reason?: string }) | null = null
      try {
        const contentType = resp.headers.get('content-type')
        if (contentType?.includes('application/json')) {
          const json = await resp.json()
          msg = json.error || json.message || msg
          const err = new Error(msg) as Error & { debug?: AiStageErrorDebug; reason?: string }
          if (json.debug) {
            err.debug = json.debug
          }
          if (json.reason) {
            err.reason = json.reason
          }
          debugError = err
        } else {
          const text = await resp.text()
          msg = text || msg
        }
      } catch (err) {
        if (err instanceof Error) {
          debugError = err as Error & { debug?: AiStageErrorDebug; reason?: string }
        }
      }
      throw debugError ?? new Error(msg)
    }

    return (await resp.json()) as AiStageResponse
  }

  // Should never reach here, but satisfy TypeScript
  throw lastError ?? new Error('AI staging failed')
}

/**
 * High-level function to stage an image with a specific style.
 * Handles the AI staging request and creates a staged_images record.
 *
 * Credit handling:
 * - The worker deducts credit only after successful AI image generation
 * - If post-processing fails (R2 upload, signed URL, etc.), we refund the credit
 *
 * New behavior:
 * - Creates a new staged_images record for each staging operation
 * - Does NOT modify the original images record (which is now simplified)
 * - Stores full prompt and metadata in staged_images
 *
 * Supports both new StructureSelection-based params and legacy roomType string params.
 */
export async function stageImage(params: StageImageParams | LegacyStageImageParams): Promise<StageImageResult> {
  const { imageId, originalUrl, style } = params

  // Get a fresh auth token (force-refresh to avoid stale tokens)
  const { token, userId } = await getFreshToken()

  // Add breadcrumb for staging start
  addStagingBreadcrumb('start', { imageId, style })

  // Build request payload for async staging
  let request: AiStageRequestAsync

  if ('structureSelection' in params && params.structureSelection) {
    const mode = params.mode || 'stage'
    let styleSource: StyleSource | undefined
    if (mode === 'stage') {
      if (params.userStyleId && params.userStylePromptTemplate) {
        styleSource = { type: 'user', promptTemplate: params.userStylePromptTemplate }
      } else if (params.baseStyleId) {
        styleSource = { type: 'base', styleId: params.baseStyleId }
      } else {
        styleSource = { type: 'legacy', slug: style }
      }
    }

    request = {
      apiVersion: '3.0',
      structureTypeId: params.structureSelection.typeId,
      styleSource,
      mode,
      customInstructions: params.customInstructions,
      images: [originalUrl],
      variants: 1,
      imageId,
      userId,
      styleLabel: mode === 'declutter' ? 'no_furniture' : style,
      baseStyleId: mode === 'declutter' ? null : ('baseStyleId' in params ? params.baseStyleId : null),
      userStyleId: mode === 'declutter' ? null : ('userStyleId' in params ? params.userStyleId : null),
    }
  } else if ('roomType' in params) {
    const legacyRoomToStructure: Record<string, string> = {
      living_room: 'living_room',
      bedroom: 'secondary_bedroom',
      kitchen: 'kitchen_island',
      dining_room: 'dining_room',
      home_office: 'home_office',
      bathroom: 'full_bath',
      other: 'living_room',
    }
    const structureTypeId = legacyRoomToStructure[params.roomType] || 'living_room'

    request = {
      apiVersion: '3.0',
      structureTypeId,
      styleSource: { type: 'legacy', slug: style },
      mode: 'stage',
      images: [originalUrl],
      variants: 1,
      imageId,
      userId,
      styleLabel: style,
    }
  } else {
    throw new Error('Either structureSelection or roomType must be provided')
  }

  const result = await stageImageAsync(request, { authToken: token })
  const signedUrl = await getSignedUrl(result.stagedPath || '')
  if (!signedUrl) {
    throw new Error('Failed to get signed URL for staged image')
  }

  addStagingBreadcrumb('complete', { imageId, style, stagedImageId: result.stagedImageId })

  return {
    stagedUrl: signedUrl,
    stagedPath: result.stagedPath,
    imageId,
    stagedImageId: result.stagedImageId,
  }
}
